import React, { useEffect, useState } from "react";
import { Box } from "@material-ui/core";
import _ from "lodash";
import { useRouteMatch } from "react-router";

import withAuthority from "../../../../components/Auth/withAuthority";
import { getCookie } from "../../../../utils/cookies";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import Authorities from "../../../../auth/authorities";
import { fetchAllDriverSessionReportInfo } from "../../../../services/salesApp/driverSessionSummaryReport";
import SaleReportOneDate from "../../../../components/common/SaleReportOneDate";
import HourlySalesReportNode from "./HourlySalesReportNode";
import SaleReport from "../../../../components/common/SaleReport";
import {
  hourlySalesFilterObject,
  salesItemsFilterObject,
} from "../../../../utils/consts/list";
import { fetchAllItemsReportVersion2Info } from "../../../../services/salesApp/itemsReportService";
import { fetchHourlySalesReport } from "../../../../services/salesApp/hourlySalesReport";
import { getAllDeviceIdInfo } from "../../../../services/salesApp/shiftService";

/**
 * HourlySalesReport Component:
 *
 * This component manages the display of shift-related information and interactions,
 * including filtering, sorting, pagination, and error handling. It fetches data from
 * backend APIs and renders shift reports and lists accordingly. The component integrates
 * with user authentication to control access based on user permissions.
 */
const HourlySalesReport: React.FunctionComponent<{}> = () => {
  const [driverSummaryNodeList, setDriverSummaryNodeList] = useState<any>([]);
  const [error, setError] = useState("");
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [weeklySales, setWeeklySales] = useState<any>({});
  const [weeklySalesCountValue, setWeeklySalesCountValue] = useState<any>({});
  const [weeklySalesTotalValue, setWeeklySalesTotalValue] = useState<any>({});
  const [filterData, setFilterData] = useState("");
  const [filterDetails, setFilterDetails] = useState("");
  const [openFilterCard, setOpenFilterCard] = useState(false);
  const [locationSelectorList, setLocationSelectorList] = useState<any>([]);
  const [isGetLocationInfo, setIsGetLocationInfo] = useState(false);
  const [locationSelectedList, setLocationSelectedList] = useState([]);
  const [deviceSelectorList, setDeviceSelectorList] = useState<any>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const extractDateRange = (input: any) => {
    const parts = input.split(",");

    let startDate: any = null;
    let endDate: any = null;

    parts.forEach((part: any) => {
      const match = part.match(/date([<>]=)(\d{4}-\d{2}-\d{2})/);
      if (match) {
        const [, operator, date] = match;
        if (operator === ">=") {
          if (!startDate || date > startDate) {
            startDate = date;
          }
        } else if (operator === "<=") {
          if (!endDate || date < endDate) {
            endDate = date;
          }
        }
      }
    });

    return { startDate, endDate };
  };

  const match: any = useRouteMatch();
  const idToken = getCookie("idToken");

  /**
   * Convert device ID response to selector list format
   * @param {Object} deviceData - Raw device data from API
   * @returns {Array} - Formatted device list with id and label
   */
  const convertDeviceIdToSelectorList = (deviceData: any) => {
    const deviceList: any = [];
    const uniqueLabels = new Set(); // Track unique labels

    // Check if deviceData is an object
    if (!_.isEmpty(deviceData) && typeof deviceData === "object") {
      // Iterate through the object values
      Object.values(deviceData).forEach((device: any) => {
        // Only include devices with a valid dev_id
        if (device.dev_id && device.dev_id !== "-99") {
          // Use handleTerminal to get the device label
          const deviceLabel = `T-${device.prefix}`;

          // Create label using dev_id and the terminal name
          let label = deviceLabel;

          // Check if this label already exists
          if (!uniqueLabels.has(label)) {
            uniqueLabels.add(label); // Add to set to track duplicates

            console.log("devicedevice", device);
            deviceList.push({
              id: device.dev_id,
              label: label,
              // Keep original data for reference
              ...device,
            });
          }
        }
      });
    }

    // Sort by dev_id (numeric)
    deviceList.sort((a: any, b: any) => {
      return parseInt(a.id) - parseInt(b.id);
    });

    return deviceList;
  };

  const getAllFilterLocation = async () => {
    // Call the API to fetch all filter locations
    fetchAllFilterLocations("")
      .then((res) => {
        // If the request is successful
        let locationList: any = [];
        // Check if the response data is not empty
        // Iterate over each location in the response data
        res.data.data.map((location: any) => {
          // Push location details to the locationList array
          locationList.push({
            id: location.id,
            label: location.businessDisplayName,
          });
        });

        // Update the location selector list with fetched locations
        setLocationSelectorList(locationList);
        // Set the flag to indicate that location info is fetched
        setIsGetLocationInfo(true);
      })
      .catch(() => {
        // If an error occurs during fetching, handle it by setting an error message
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      });
  };

  const getAllDeviceId = async () => {
    getAllDeviceIdInfo(match.params.locationId)
      .then((res: any) => {
        // Convert device data to selector list format using handleTerminal
        const formattedDeviceList = convertDeviceIdToSelectorList(
          res.data.data,
        );
        setDeviceSelectorList(formattedDeviceList);
      })
      .catch(() => {
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      });
  };

  /* Get all shift information using API call and response data set to driverSummaryNodeList state. */
  const getShiftFilterInfo = async (filter: any) => {
    const startEndDate = extractDateRange(filter);
    setStartDate(startEndDate.startDate);
    setEndDate(startEndDate.endDate);
    try {
      // Call the API to fetch sale item report information
      const res = await fetchHourlySalesReport(
        idToken,
        match.params.locationId,
        filter,
      );

      if (Object.values(res.data.data).length === 0) {
        setDriverSummaryNodeList({ id: 1 });
      } else {
        setDriverSummaryNodeList(res.data.data);
      }

      setIsLoadingPage(false);
      // getSaleItemReportInfoCombineModifierFalse(filter, basedOnShift, output);
      // Once the data is fetched successfully, call getGroupInfo to proceed with data processing
    } catch (err) {
      // If an error occurs during fetching, handle it by setting loading state and displaying error message
      setIsLoadingPage(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setDriverSummaryNodeList({ id: 1 });
    }
  };

  useEffect(() => {
    getAllFilterLocation();
    getAllDeviceId();
    document.title = "Sale - Hourly Sales Report";
  }, []);

  // Get filter data
  const handleFilterData = (filterData: any) => {
    setIsLoadingPage(true);
    setFilterData(filterData);
    getShiftFilterInfo(filterData);
  };

  const handleOnClickText = () => {
    setOpenFilterCard(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  const handleLocationSelectorTypingList = (searchName: any) => {};
  return (
    <>
      <Box>
        <SaleReport
          handleFilterData={handleFilterData}
          locationSelectorList={locationSelectorList}
          handleLocationSelectorTypingList={handleLocationSelectorTypingList}
          setFilterDetails={setFilterDetails}
          availableFilter={hourlySalesFilterObject}
          isOpenSkeletonLoading={isLoadingPage}
          nodeList={driverSummaryNodeList}
          filterDetails={filterDetails}
          title={""}
          isChart={false}
          isGetLocationInfo={isGetLocationInfo}
          isSaleItem={true}
          locationSelectedList={locationSelectedList}
          setLocationSelectedList={setLocationSelectedList}
          openFilterCard={openFilterCard}
          setOpenFilterCard={setOpenFilterCard}
          handleOnClickText={handleOnClickText}
          topic="Hourly Sales Report"
          isReport={true}
          isWeekDateRange={false}
          isNeedDropdown={true}
          isNeedTimeout={false}
          isWeek={true}
          deviceSelectorList={deviceSelectorList}
        >
          <HourlySalesReportNode
            isLoading={isLoadingPage}
            startDate={startDate}
            endDate={endDate}
            filterDetails={filterDetails}
            locationSelectedList={locationSelectedList}
            handleOnClickText={handleOnClickText}
            driverSummaryNodeList={driverSummaryNodeList}
            weeklySales={weeklySales}
            weeklySalesCountValue={weeklySalesCountValue}
            weeklySalesTotalValue={weeklySalesTotalValue}
          />
          {/* {!_.isEmpty(driverSummaryNodeList) && (
            <Pagination
              handleChangePaginationUsingSelector={
                handleChangePaginationUsingSelector
              }
              handleChangePaginationUsingPageNumber={
                handleChangePaginationUsingPageNumber
              }
              totalPages={totalPages}
              currentPage={currentPage}
              pageSize={pageSize}
              isShift={true}
              totalResult={totalResult}
            />
          )} */}
        </SaleReport>

        <DefaultAlert
          open={!!error}
          handleClose={() => setError("")}
          message={error}
          severity="error"
        />
      </Box>
    </>
  );
};

export default withAuthority(HourlySalesReport, Authorities.SALE_READ);
